// src/pages/CartPage.jsx
import React, { useState, useEffect } from "react";
import { useCart } from "../context/CartContext";
import OnScreenKeyboard from "../components/Common/OnScreenKeyboard";
import CartHeader from "../components/Cart/cart_header";
import EmptyCart from "../components/Cart/empty_cart";
import CartItem from "../components/Cart/cart_item";
import CartSummary from "../components/Cart/cart_summary";
import CheckoutForm from "../components/Cart/checkout_form";
import SuccessPage from "../components/Cart/success_page";
import LoadingOverlay from "../components/Cart/loading_overlay";

import "../styles/CartPage.css";
import "react-simple-keyboard/build/css/index.css";

const ORDER_ENDPOINT = "https://dat-photo-service.duckdns.org/order/zip";

const CartPage = () => {
  const {
    items = [],
    catalogItems = [],
    email = '',
    name = '',
    notes = '',
    usbDriveOption = false,
    setEmail,
    setName,
    setNotes,
    setUsbDriveOption,
    clearCart,
    clearCatalogCart,
    removeFromCart,
    removeFromCatalogCart,
    getTotalPrice,
    getCatalogTotalPrice
  } = useCart();

  const [emailInput, setEmailInput] = useState(email);
  const [emailError, setEmailError] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [zipUrl, setZipUrl] = useState("");
  const [submittedItems, setSubmittedItems] = useState([]);
  const [submittedTotal, setSubmittedTotal] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [showCountdown, setShowCountdown] = useState(false);
  const [nameError, setNameError] = useState("");
  const [notesInput, setNotesInput] = useState(notes);
  const [focusedInput, setFocusedInput] = useState(null);

  // Detect mobile devices more reliably
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileDevice =
        window.innerWidth <= 768 ||
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        ) ||
        "ontouchstart" in window;

      setIsMobile(isMobileDevice);
      console.log(
        "Mobile detected:",
        isMobileDevice,
        "Width:",
        window.innerWidth
      );
    };

    checkIfMobile();
    window.addEventListener("resize", checkIfMobile);

    return () => window.removeEventListener("resize", checkIfMobile);
  }, []);

  // Handle countdown when cart is empty
  useEffect(() => {
    if (items.length === 0 && catalogItems.length === 0 && !isSubmitted) {
      setShowCountdown(true);
      setCountdown(3);

      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            // Navigate to root page after countdown
            window.location.href = "/";
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    } else {
      setShowCountdown(false);
    }
  }, [items.length, catalogItems.length, isSubmitted]);

  const validateEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmailInput(value);
    setEmail(value);
    if (emailError && validateEmail(value)) setEmailError("");
  };

  const handleClearEmail = () => {
    setEmailInput("");
    setEmail("");
    setEmailError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate name if catalog items exist
    if (catalogItems.length > 0 && !name) {
      return setNameError("Please enter a name for this order.");
    }
    setNameError("");

    // Check for Pick 5 items validation
    const pick5Items = catalogItems.filter(item => 
      item.title.includes("Pick 5")
    );
    const pick5Quantity = pick5Items.reduce((total, item) => total + (item.quantity || 1), 0);
    if (pick5Quantity > 0 && pick5Quantity !== 5) {
      return setNameError(
        `The "Pick 5" promotion requires exactly 5 items (you have ${pick5Quantity})`
      );
    }

    // Validate email if photo items exist
    if (items.length > 0 && !validateEmail(emailInput)) {
      return setEmailError("Please enter a valid email address");
    }

    // Check if cart is empty
    if (items.length === 0 && catalogItems.length === 0) {
      return setEmailError("Your cart is empty");
    }

    setIsSubmitting(true);
    setEmailError("");

    try {
      // Submit catalog order if items exist
      let catalogOrderId = null;
      if (catalogItems.length > 0) {
        catalogOrderId = crypto.randomUUID();
        
        // Expand items based on quantity - create separate line items for each quantity
        const expandedItems = [];
        catalogItems.forEach(item => {
          const quantity = item.quantity || 1;
          for (let i = 0; i < quantity; i++) {
            expandedItems.push(item.title);
          }
        });
        
        let catalogTotal = getCatalogTotalPrice();
        if (usbDriveOption) catalogTotal += 10;
        
        const orderData = {
          customer: name,
          items: expandedItems,
          total: catalogTotal,
          timestamp: new Date().toISOString(),
          notes: usbDriveOption ? `${notesInput}\n\nINCLUDE USB DRIVE` : notesInput
        };

        const catalogRes = await fetch('https://gun-orders-worker.t9e2i3kal.workers.dev/kv', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: `order_${catalogOrderId}`,
            value: orderData
          })
        });

        if (!catalogRes.ok) throw new Error('Failed to submit catalog order');
      }

      // Submit photo order if items exist
      let photoOrderUrl = null;
      if (items.length > 0) {
        let total = getTotalPrice();
        if (usbDriveOption) total += 10;
        const payload = {
          email: emailInput,
          items: items.map((i) => i.url),
          total,
          notes: usbDriveOption ? `${notesInput}\n\nINCLUDE USB DRIVE` : notesInput,
        };

        const photoRes = await fetch(ORDER_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        if (!photoRes.ok) {
          throw new Error(`Server responded ${photoRes.status} ${photoRes.statusText}`);
        }

        const { url } = await photoRes.json();
        photoOrderUrl = url;
      }

      // Set success state
      let total = getCatalogTotalPrice() + getTotalPrice();
      if (usbDriveOption) total += 10;
      setSubmittedItems([...catalogItems, ...items]);
      setSubmittedTotal(total);
      setZipUrl(photoOrderUrl);
      setIsSubmitted(true);

      // Clear both carts
      clearCart();
      clearCatalogCart();
      setName("");
      setEmailInput("");
      setEmail("");
      setNotes("");
      setNotesInput("");
    } catch (err) {
      console.error(err);
      setEmailError(`Order submission failed: ${err.message}`);
      setIsSubmitting(false);
    }
  };

  const handleClearCart = () => {
    clearCart();
    clearCatalogCart();
    setIsSubmitted(false);
    setZipUrl("");
    setSubmittedItems([]);
    setSubmittedTotal(0);
    handleClearEmail();
  };

  if (isSubmitted) {
    return (
      <SuccessPage
        isMobile={isMobile}
        zipUrl={zipUrl}
        submittedItems={submittedItems}
        submittedTotal={submittedTotal}
        notesInput={notesInput}
        usbDriveOption={usbDriveOption}
      />
    );
  }

  return (
    <div className={`cart-page ${isMobile ? "mobile-no-keyboard" : ""}`}>
      <LoadingOverlay isSubmitting={isSubmitting} />

      <div className={`cart-container ${isSubmitting ? "disabled" : ""}`}>
        {(items.length > 0 || catalogItems.length > 0) && (
          <CartHeader items={items} catalogItems={catalogItems} />
        )}

        {items.length === 0 && catalogItems.length === 0 ? (
          <EmptyCart showCountdown={showCountdown} countdown={countdown} />
        ) : (
          <>
            <div className="cart-items">
              {items.map((item) => (
                <CartItem
                  key={item.key}
                  item={item}
                  onRemove={() => removeFromCart(item.key)}
                  isSubmitting={isSubmitting}
                  type="photo"
                />
              ))}

              {catalogItems.map((item) => (
                <CartItem
                  key={item.id}
                  item={item}
                  onRemove={() => removeFromCatalogCart(item.id)}
                  isSubmitting={isSubmitting}
                  type="catalog"
                />
              ))}
            </div>

            <CartSummary
              photoTotal={getTotalPrice()}
              catalogTotal={getCatalogTotalPrice()}
              hasPhotoItems={items.length > 0}
              hasCatalogItems={catalogItems.length > 0}
              usbDriveOption={usbDriveOption}
            />

            <CheckoutForm
              catalogItems={catalogItems}
              items={items}
              name={name}
              setName={setName}
              nameError={nameError}
              emailInput={emailInput}
              handleEmailChange={handleEmailChange}
              handleClearEmail={handleClearEmail}
              emailError={emailError}
              notesInput={notesInput}
              setNotesInput={setNotesInput}
              setNotes={setNotes}
              handleSubmit={handleSubmit}
              handleClearCart={handleClearCart}
              isSubmitting={isSubmitting}
              focusedInput={focusedInput}
              setFocusedInput={setFocusedInput}
              usbDriveOption={usbDriveOption}
              setUsbDriveOption={setUsbDriveOption}
            />
          </>
        )}
      </div>

      {!isMobile && (items.length > 0 || catalogItems.length > 0) && (
        <OnScreenKeyboard 
          isDisabled={isSubmitting}
          isEmailMode={focusedInput === 'email'}
        />
      )}
    </div>
  );
};

export default CartPage;
